/**
 * City + Situation combo page configuration.
 * 5 top cities × 10 high-intent situations = 50 hyper-targeted landing pages.
 */

export const comboTargetCities = [
  "oklahoma-city",
  "edmond",
  "norman",
  "tulsa",
  "moore",
] as const;

export const comboTargetSituations = [
  "facing-foreclosure",
  "inherited-a-house",
  "divorce",
  "relocating",
  "storm-damage",
  "major-repairs-needed",
  "behind-on-payments",
  "code-violations",
  "hoarder-home",
  "vacant-property",
] as const;

/** Generate a unique, keyword-rich meta title */
export const comboMetaTitle = (cityName: string, sitName: string): string =>
  `${sitName} in ${cityName}? Sell Your House Fast for Cash | House Kings`;

/** Generate a unique, CTR-optimized meta description */
export const comboMetaDescription = (cityName: string, sitName: string): string =>
  `${sitName} in ${cityName}? House Kings buys houses fast for cash. ✓ Cash offer in 24hrs ✓ Close in 7 days ✓ No fees ✓ No repairs. Trusted local cash home buyers since 2006.`;

/** Generate the H1 for combo pages */
export const comboH1 = (cityName: string, sitName: string): string =>
  `Sell Your ${cityName} House Fast — ${sitName}`;

/** Contextual intro paragraph combining city + situation */
export const comboIntro = (cityName: string, sitName: string, cityIntro: string, sitIntro: string): string => {
  return `Dealing with ${sitName.toLowerCase()} in ${cityName}? You're not alone — and you have options. House Kings has been helping ${cityName} homeowners since 2006, and we specialize in exactly this situation. ${sitIntro}`;
};

/** Generate FAQ items specific to the city+situation combo */
export const comboFaqs = (cityName: string, sitName: string) => [
  {
    question: `How fast can I sell my ${cityName} house if I'm ${sitName.toLowerCase()}?`,
    answer: `House Kings can make you a cash offer within 24 hours and close in as little as 7 days. We specialize in helping ${cityName} homeowners dealing with ${sitName.toLowerCase()}.`,
  },
  {
    question: `Do I need to make repairs before selling my ${cityName} home?`,
    answer: `No repairs needed! We buy ${cityName} houses in any condition — as-is, damaged, outdated, full of belongings. We handle everything after closing.`,
  },
  {
    question: `Are there fees when selling my ${cityName} house to House Kings?`,
    answer: `Zero fees. No realtor commissions (saving you 6%), no closing costs, no hidden charges. The cash offer we make is the amount you receive at closing.`,
  },
  {
    question: `Why should I sell to House Kings instead of listing with a ${cityName} realtor?`,
    answer: `When you're dealing with ${sitName.toLowerCase()}, time matters. Listing with a realtor means months of showings, 6% commissions, and repair requests. With House Kings, you get a guaranteed cash offer in 24 hours and close in days.`,
  },
];

/**
 * Generates 2 unique paragraphs of hyper-local content combining a specific
 * neighborhood with the situation. This makes each of the 50 combo pages
 * substantively unique to avoid Google's "thin/duplicate content" flag.
 */
export const comboLocalContent = (
  cityName: string,
  sitName: string,
  neighborhoods: string[],
  localLandmark: string,
): string[] => {
  const sit = sitName.toLowerCase();
  const n1 = neighborhoods[0] || cityName;
  const n2 = neighborhoods[1] || cityName;
  const n3 = neighborhoods[2] || cityName;
  return [
    `Across ${cityName} — from ${n1} to ${n2} to ${n3} — we've worked with homeowners facing ${sit} in every type of property: vintage bungalows, ranch homes, fixer-uppers, and newer builds. Because we're local to the metro and just a short drive from ${localLandmark}, we can be at your property within a day or two to do a walkthrough and put a real cash number in your hand.`,
    `${cityName}'s housing market moves differently than the rest of Oklahoma. Property values, comparable sales, repair costs, and even title histories vary block by block. When you're dealing with ${sit}, you don't have months to learn the market — you need a buyer who already knows it. House Kings has been buying ${cityName} houses since 2006, which means our cash offer reflects what your home is actually worth in your specific ${cityName} neighborhood, not a generic statewide average.`,
  ];
};
